import { getDb } from "@/services/storage/sqlite/connection";
import { logger } from "@/services/logger";
import type { KnowledgeScope, KnowledgeSearchResult } from "@/types";
import { bytesToFloats } from "./encoding";
import { cosineSimilarity } from "./similarity";

const log = logger.scope("vector-store");

interface ChunkEmbeddingRow {
  id: string;
  document_id: string;
  content: string;
  embedding: number[] | null;
  name: string;
  source_path: string;
}

async function candidateRows(scope: KnowledgeScope): Promise<ChunkEmbeddingRow[]> {
  const db = await getDb();
  const base = `SELECT ch.id, ch.document_id, ch.content, ch.embedding, d.name, d.source_path
                FROM chunks ch JOIN documents d ON d.id = ch.document_id
                WHERE d.status = 'ready'`;

  if (scope.mode === "collections" && scope.collectionIds && scope.collectionIds.length > 0) {
    const placeholders = scope.collectionIds.map((_, i) => `$${i + 1}`).join(",");
    return db.select<ChunkEmbeddingRow[]>(
      `${base} AND d.collection_id IN (${placeholders})`,
      scope.collectionIds,
    );
  }

  return db.select<ChunkEmbeddingRow[]>(base);
}

/**
 * Brute-force cosine-similarity search over every candidate chunk's stored
 * embedding — adequate for a personal knowledge base, not an ANN index. See
 * `KNOWLEDGE_SYSTEM.md` for the scaling note and the swap-in path if this
 * ever needs to become a real index; callers only depend on this function's
 * signature, not its implementation.
 */
export async function search(
  queryEmbedding: Float32Array,
  scope: KnowledgeScope,
  topK = 6,
): Promise<KnowledgeSearchResult[]> {
  let rows = await candidateRows(scope);

  if (scope.mode === "folder" && scope.folderPath) {
    rows = rows.filter((r) => r.source_path.startsWith(scope.folderPath!));
  }

  const scored: KnowledgeSearchResult[] = [];
  for (const row of rows) {
    if (!row.embedding) continue;
    let vector: Float32Array;
    try {
      vector = bytesToFloats(row.embedding);
    } catch (err) {
      log.warn("Skipping chunk with unreadable embedding", { chunkId: row.id, err });
      continue;
    }
    scored.push({
      chunk: { id: row.id, documentId: row.document_id, seq: 0, content: row.content },
      documentId: row.document_id,
      documentName: row.name,
      score: cosineSimilarity(queryEmbedding, vector),
    });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, topK);
}
